import React, { useEffect, useRef } from 'react';

const SITE_KEY = '0x4AAAAAADey_ufapu04Y6nT';

interface TurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}

declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: object) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export default function Turnstile({ onVerify, onExpire, onError }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId     = useRef<string | null>(null);
  const onVerifyRef  = useRef(onVerify);
  const onExpireRef  = useRef(onExpire);
  const onErrorRef   = useRef(onError);

  // Keep refs current on every render — no stale closures
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current  = onError;
  });

  useEffect(() => {
    const render = () => {
      if (!containerRef.current || !window.turnstile) return;
      if (widgetId.current) {
        try { window.turnstile.remove(widgetId.current); } catch {}
      }
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey:  SITE_KEY,
        theme:    'dark',
        callback: (token: string) => onVerifyRef.current(token),
        'expired-callback': () => { onExpireRef.current?.(); widgetId.current = null; },
        'error-callback':   () => { onErrorRef.current?.();  widgetId.current = null; },
      });
    };

    if (window.turnstile) {
      render();
    } else {
      window.onTurnstileLoad = render;
      if (!document.getElementById('turnstile-script')) {
        const script = document.createElement('script');
        script.id  = 'turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
        script.async = true;
        document.head.appendChild(script);
      }
    }

    return () => {
      if (widgetId.current) {
        try { window.turnstile.remove(widgetId.current); } catch {}
      }
    };
  }, []);

  return <div ref={containerRef} className="turnstile-widget" />;
}